import { StrictMode, useEffect, type ReactNode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";

import { keepHydratedConsoleQueryCache, persistConsoleQueryCache } from "./lib/query-cache";
import { capturePageViews } from "./posthog";
import { createConsoleRouter } from "./router";
import "./styles.css";

/* The one router, with the one persisted cache. `createConsoleRouter` restores the
 * snapshot before it returns, so the first render is already the last known screen — for
 * everything except identity, which is never kept on disk and is always a door call. */
const root = document.getElementById("root")!;
const serverRendered = root.dataset.serverRendered === "true";
// A private server document already carries this request's authorized data. Never merge
// an unattributed disk snapshot into it before hydration confirms that identity.
const router = createConsoleRouter(undefined, serverRendered ? undefined : persistConsoleQueryCache);

function InteractiveRoot({ children }: { readonly children: ReactNode }) {
  useEffect(() => {
    keepHydratedConsoleQueryCache(router.options.context.queryClient);
    root.dataset.hydrated = "true";
    return () => { delete root.dataset.hydrated; };
  }, []);
  return children;
}

/* Navigation is a product fact, so the product states it — here, at the composition root,
 * for the same reason persistence is injected rather than assumed: a suite that builds its
 * own router does not inherit the browser's analytics. A build with no PostHog settings
 * captures nothing and still navigates. */
capturePageViews(router);

/* A project's address opens with its rail's agent tree, a chunk of its own. The shell's own
 * module asks for it too, but that module arrives only after the identity read, and the
 * tree then mounts before its chunk is here: React holds a suspended reveal for 300 ms and
 * the rail paints without its agents. Asked for here, at the entry, it is here first. */
if (/^\/p\/[^/]+\/[^/]+(\/|$)/.test(window.location.pathname)) {
  void import("./workspaces/agents/ChatsRailTree");
}

if (serverRendered) {
  void import("@tanstack/react-router/ssr/client").then(({ RouterClient }) => {
    hydrateRoot(root, <StrictMode><InteractiveRoot><RouterClient router={router} /></InteractiveRoot></StrictMode>);
  });
} else {
  createRoot(root).render(<StrictMode><RouterProvider router={router} /></StrictMode>);
}
